"use client";

import { useCallback, useEffect, useState } from "react";
import { Button } from "@/components/ui/button";

const DISMISS_KEY = "pwa-install-dismissed-until";
const DISMISS_DURATION_MS = 1000 * 60 * 60 * 24; // 24 hours
const AUTO_SHOW_DELAY_MS = 2000; // 2 seconds delay before auto-showing
// Once the app is actually installed, stop nagging in the originating tab
// for a long time (the installed instance reports standalone on its own).
const INSTALLED_SUPPRESS_MS = 1000 * 60 * 60 * 24 * 365; // 1 year

type Platform = "ios" | "prompt" | null;

const isIosDevice = () => {
  if (typeof window === "undefined") return false;
  const ua = window.navigator.userAgent;
  // iPhone/iPod always match; iPadOS 13+ reports as Mac with touch support
  const iOS = /iPhone|iPad|iPod/.test(ua);
  const iPadOS =
    ua.includes("Macintosh") && "ontouchend" in document;
  return iOS || iPadOS;
};

const isStandalone = () => {
  if (typeof window === "undefined") return false;
  // iOS uses the legacy navigator.standalone; everyone else uses the media query
  const navStandalone = (window.navigator as Navigator & {
    standalone?: boolean;
  }).standalone;
  return (
    navStandalone === true ||
    window.matchMedia("(display-mode: standalone)").matches
  );
};

export default function PWAInstallHandler() {
  const [deferredPrompt, setDeferredPrompt] = useState<any>(null);
  const [platform, setPlatform] = useState<Platform>(null);
  const [showModal, setShowModal] = useState(false);

  const shouldSuppressPrompt = useCallback(() => {
    if (typeof window === "undefined") return false;
    const storedValue = window.localStorage.getItem(DISMISS_KEY);
    if (!storedValue) return false;

    const dismissedUntil = Number(storedValue);
    if (!Number.isFinite(dismissedUntil)) {
      window.localStorage.removeItem(DISMISS_KEY);
      return false;
    }

    const isStillSuppressed = dismissedUntil > Date.now();
    if (!isStillSuppressed) {
      window.localStorage.removeItem(DISMISS_KEY);
    }
    return isStillSuppressed;
  }, []);

  // iOS detection: Safari never fires beforeinstallprompt, so flag the platform
  // up front and show the manual Add to Home Screen instructions instead.
  useEffect(() => {
    if (isStandalone()) return;
    if (isIosDevice()) setPlatform("ios");
  }, []);

  useEffect(() => {
    const handleBeforeInstallPrompt = (e: any) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setPlatform("prompt");
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
    return () =>
      window.removeEventListener(
        "beforeinstallprompt",
        handleBeforeInstallPrompt
      );
  }, []);

  // Stop prompting the instant the app is installed (covers the tab that
  // triggered the install, before it relaunches in standalone mode).
  useEffect(() => {
    const handleAppInstalled = () => {
      setShowModal(false);
      setPlatform(null);
      setDeferredPrompt(null);
      if (typeof window !== "undefined") {
        window.localStorage.setItem(
          DISMISS_KEY,
          String(Date.now() + INSTALLED_SUPPRESS_MS)
        );
      }
    };

    window.addEventListener("appinstalled", handleAppInstalled);
    return () => window.removeEventListener("appinstalled", handleAppInstalled);
  }, []);

  // Auto-show modal once per day for users who haven't installed
  useEffect(() => {
    if (!platform) return;
    if (isStandalone()) return; // already installed → never ask
    if (shouldSuppressPrompt()) return;

    const timer = setTimeout(() => {
      setShowModal(true);
    }, AUTO_SHOW_DELAY_MS);

    return () => clearTimeout(timer);
  }, [platform, shouldSuppressPrompt]);

  const handleInstallClick = async () => {
    if (!deferredPrompt) return;

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;

    if (outcome === "accepted") {
      if (typeof window !== "undefined") {
        window.localStorage.removeItem(DISMISS_KEY);
      }
    } else if (typeof window !== "undefined") {
      window.localStorage.setItem(
        DISMISS_KEY,
        String(Date.now() + DISMISS_DURATION_MS)
      );
    }
    setPlatform(null);
    setDeferredPrompt(null);
    setShowModal(false);
  };

  const handleDismiss = () => {
    setShowModal(false);
    setPlatform(null);
    setDeferredPrompt(null);

    if (typeof window !== "undefined") {
      window.localStorage.setItem(
        DISMISS_KEY,
        String(Date.now() + DISMISS_DURATION_MS)
      );
    }
  };

  if (!showModal) return null;

  const isIos = platform === "ios";

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
      <div className="relative w-full max-w-md rounded-3xl border-2 border-[#4c4c4c] bg-[#fffdfa] p-8 shadow-[6px_6px_0_#4c4c4c] text-center">
        <button
          type="button"
          onClick={handleDismiss}
          className="absolute right-4 top-4 rounded-full border border-[#4c4c4c]/30 bg-white/70 p-1 text-[#4c4c4c] transition-colors hover:bg-white"
          aria-label="Close install prompt"
        >
          <svg
            className="h-4 w-4"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>

        <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full border-2 border-[#4c4c4c] bg-[#ffd372]/50">
          <img src="/logo.png" alt="Install" className="h-10 w-10" />
        </div>

        <h2 className="font-winky text-2xl font-semibold text-[#4c4c4c]">
          Take Hoku Anywhere
        </h2>

        {isIos ? (
          <>
            <p className="mt-3 font-patrick-hand text-base text-[#4c4c4c]/80">
              Add Hey Hoku to your home screen for the full-screen wardrobe
              experience.
            </p>
            <ol className="mt-5 space-y-3 text-left font-patrick-hand text-base text-[#4c4c4c]">
              <li className="flex items-start gap-3">
                <span className="mt-0.5 flex h-6 w-6 flex-none items-center justify-center rounded-full border-2 border-[#4c4c4c] bg-[#ffd372] text-sm font-semibold">
                  1
                </span>
                <span>
                  Tap the{" "}
                  <span className="inline-flex items-center gap-1 font-semibold">
                    Share
                    <svg
                      className="inline h-4 w-4"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" />
                      <polyline points="16 6 12 2 8 6" />
                      <line x1="12" y1="2" x2="12" y2="15" />
                    </svg>
                  </span>{" "}
                  button in Safari&apos;s toolbar.
                </span>
              </li>
              <li className="flex items-start gap-3">
                <span className="mt-0.5 flex h-6 w-6 flex-none items-center justify-center rounded-full border-2 border-[#4c4c4c] bg-[#ffd372] text-sm font-semibold">
                  2
                </span>
                <span>
                  Scroll and tap{" "}
                  <span className="font-semibold">Add to Home Screen</span>.
                </span>
              </li>
              <li className="flex items-start gap-3">
                <span className="mt-0.5 flex h-6 w-6 flex-none items-center justify-center rounded-full border-2 border-[#4c4c4c] bg-[#ffd372] text-sm font-semibold">
                  3
                </span>
                <span>
                  Tap <span className="font-semibold">Add</span> in the top
                  right.
                </span>
              </li>
            </ol>
            <div className="mt-8 flex justify-center">
              <Button
                type="button"
                onClick={handleDismiss}
                className="h-12 w-full rounded-lg border-2 border-[#4c4c4c] bg-[#ffd372] font-winky text-base text-[#4c4c4c] transition-all duration-200 ease-in-out hover:bg-[#ffe58a] hover:scale-105 hover:-translate-y-[2px] active:scale-95 active:translate-y-[2px] shadow-[3px_3px_0_#4c4c4c]"
              >
                Got it
              </Button>
            </div>
          </>
        ) : (
          <>
            <p className="mt-3 font-patrick-hand text-base text-[#4c4c4c]/80">
              Install the Hey Hoku app for lightning-fast access, offline magic,
              and the full-screen wardrobe experience.
            </p>
            <div className="mt-8 flex flex-row flex-wrap gap-3 justify-center">
              <Button
                type="button"
                onClick={handleDismiss}
                className="h-12 flex-1 rounded-lg border-2 border-[#4c4c4c] bg-white font-winky text-base text-[#4c4c4c] transition-all duration-200 ease-in-out hover:bg-[#f7f3eb] hover:scale-105 hover:-translate-y-[2px] active:scale-95 active:translate-y-[2px] shadow-[2px_2px_0_#4c4c4c]"
              >
                Maybe Later
              </Button>
              <Button
                type="button"
                onClick={handleInstallClick}
                className="h-12 flex-1 rounded-lg border-2 border-[#4c4c4c] bg-[#ffd372] font-winky text-base text-[#4c4c4c] transition-all duration-200 ease-in-out hover:bg-[#ffe58a] hover:scale-105 hover:-translate-y-[2px] active:scale-95 active:translate-y-[2px] shadow-[3px_3px_0_#4c4c4c]"
              >
                Install App
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
